import { Injectable } from '@angular/core';
import { QueryOptions } from '@mkp/shared/util-pagination';
import { CoreListEnvelop } from '@mkp/shared/models';
import { BaseHttpResource } from '@mkp/shared/data-access';
import { expand, forkJoin, map, Observable, of, reduce, switchMap } from 'rxjs';
import { toViewModel } from './vacancy.mapper';
import { VacancyLinks } from '@mkp/vacancy/models';
import { VacancyDto, VacancyModel, VacancyPatchBody } from './vacancy.models';

export type VacancyList = Pick<CoreListEnvelop<VacancyModel>, 'totalCount' | 'limit' | 'offset'> & {
  vacancies: VacancyModel[];
};

interface PaginatedVacancyList {
  vacancies: VacancyModel[];
  _links: VacancyLinks;
  filter: string;
  totalCount: number;
}

const VACANCY_PAGE_SIZE = 200;

@Injectable({ providedIn: 'root' })
export class VacancyResource extends BaseHttpResource<VacancyDto, CoreListEnvelop<VacancyDto>> {
  constructor() {
    super('vacancy');
  }

  list(): Observable<VacancyList> {
    return this.http.get<CoreListEnvelop<VacancyDto>>(this.url).pipe(
      map(({ limit, offset, totalCount, _embedded: { results } }) => ({
        limit,
        offset,
        totalCount,
        vacancies: results.map((vacancyDto) => toViewModel(vacancyDto)),
      }))
    );
  }

  /**
   * Returns a list of vacancies
   * with pagination details included.
   */
  public listPaginated(params: QueryOptions, accountId?: string): Observable<PaginatedVacancyList> {
    return this.getWithQuery({ params, withAccountId: accountId }).pipe(
      map(({ _embedded, filter, _links, totalCount }) => ({
        vacancies: _embedded.results.map((dto) => toViewModel(dto)),
        _links: _links as VacancyLinks,
        filter,
        totalCount,
      }))
    );
  }

  getVacanciesByRecruiterId(recruiterId: string, accountId: string): Observable<VacancyDto[]> {
    const filter = `recruiter.id==${recruiterId}`;

    return this.fetchVacancyPage(filter, accountId, 0).pipe(
      expand((page) => {
        const nextOffset = page.offset + page._embedded.results.length;
        const hasMore = page._embedded.results.length > 0 && nextOffset < page.totalCount;
        return hasMore ? this.fetchVacancyPage(filter, accountId, nextOffset) : of();
      }),
      reduce<CoreListEnvelop<VacancyDto>, VacancyDto[]>(
        (all, page) => [...all, ...page._embedded.results],
        []
      )
    );
  }

  removeRecruiterFromVacancies(recruiterId: string, accountId: string): Observable<VacancyDto[]> {
    return this.getVacanciesByRecruiterId(recruiterId, accountId).pipe(
      switchMap((vacancies) => {
        if (vacancies.length === 0) {
          return of([]);
        }

        const updateRequests = vacancies.map((vacancy) =>
          this.update(vacancy.id, {
            recruiter: null,
            recruiterId: null,
            _version: vacancy._version,
          } satisfies VacancyPatchBody)
        );

        return forkJoin(updateRequests);
      })
    );
  }

  private fetchVacancyPage(
    filter: string,
    accountId: string,
    offset: number
  ): Observable<CoreListEnvelop<VacancyDto>> {
    return this.getWithQuery({
      params: { filter, offset, limit: VACANCY_PAGE_SIZE },
      headers: { maxRetry: 1 },
      withAccountId: accountId,
    });
  }
}
